import { FacultyAttendance } from "../../../features/dashboard/types";
import { Address } from "./apis/AddressAPI";
import { Person } from "./apis/PersonAPI";
import { StaffAbsenteesByDept } from "./apis/StaffAPI";

export type Fee = {
  studentName: string;
  admissionNo: string;
  gradeName: string;
  sectionName: string;
  amount: number;
  transactionId: string;
  dateOfPayment: string;
  paymentMode: string;
  discount: number;
  terms: string;
  receiptNo: string;
  payable: number;
  balance: number;
  collectedBy: string;
  gradeTerm: string;
  previousYDiscount: number;
  currentYDiscount: number;
  previousYearDues: number;
};

// A row of GET /fee?view=list
export type FeeReceiptListItem = {
  receiptNo: number;
  studentName: string;
  admissionNo: string;
  gradeName: string;
  sectionName: string;
  terms: string;
  amount: number;
  discount: number;
  paymentMode: string;
  transactionId: string;
  dateOfPayment: string;
};

export type FeePage = {
  feeList: FeeReceiptListItem[];
  totalPages: number;
  totalElements: number;
};

export enum NoteType {
  MEDICAL_DISABILITY = "0",
  LEARNING_DISABILITY = "1",
  REGULAR_MEDICATION = "2",
}
export const GuardianType = {
  OTHER: "Other",
  FATHER: "Father",
  MOTHER: "Mother",
};
export const EmergencyType = {
  FATHER: "Father",
  MOTHER: "Mother",
  GUARDIAN: "Guardian",
  OTHER: "Other",
};
export const ActionType = {
  CREATE: "CREATE",
  UPDATE: "UPDATE",
};
export { UserRole } from "../../../auth/roles";
export type Note = {
  type: NoteType;
  comment: string;
};
export type PreviousSchool = {
  name: string | undefined;
  fromGrade: string | undefined;
  toGrade: string | undefined;
  fromYear: number | undefined;
  toYear: number | undefined;
};

export type Student = {
  name: string;
  admissionNo: string | undefined;
  gender: string;
  dob: string;
  placeOfBirth: string | undefined;
  religion: string | undefined;
  nationality: string | undefined;
  motherTongue: string | undefined;
  bloodGroup: string | undefined;
  externalId: number | undefined;
  present: Address;
  permanent: Address;
  previousSchools?: PreviousSchool[];
  father: Person;
  mother: Person;
  guardian: Person;
  notes?: Note[] | null;
  emergency: Person;
  gradeName: string | undefined;
  sectionName: string | undefined;
  active: boolean;
  totalDue?: number;
  requestFrom: string;
  permanentAddIsSameAsPresent?: boolean;
  emergencyContactRelation: string;
  guardianContactRelation: string;
  feeResponseDtos?: FeeReceipt[];
  status?: StudentStatus;
};

export const STUDENT_STATUS = {
  Registered: "REGISTERED",
  Active: "ACTIVE",
  Dropped: "DROPPED",
  Transferred: "TRANSFERRED",
} as const;

export type StudentStatus = typeof STUDENT_STATUS[keyof typeof STUDENT_STATUS];

export const ACADEMIC_STATUS = {
  Promoted: "PROMOTED",
  Repeated: "REPEATED",
  Graduated: "GRADUATED",
  Dropped: "DROPPED",
  Transferred: "TRANSFERRED",
} as const;

export type AcademicStatus = typeof ACADEMIC_STATUS[keyof typeof ACADEMIC_STATUS];

export type FeeReceipt = {
  name: string;
  admissionNo: string;
  grade: string;
  section: string;
  receiptNo: number;
  amount: number;
  discount: number;
  terms: string;
  receiptDate: string;
  paymentMode: string;
  dateOfPayment: string;
  transactionId: string;
  collectedBy: string;
  generatedBy: string;
  academicYear: string;
  guardianName: string;
  guardianMobile: string;
};

export type FeeDue = {
  studentName: string;
  admissionNo: string;
  guardianPhoneNo: number;
  gradeName: string;
  sectionName: string;
  terms: string;
  payable: number;
  academicYear: string;
  previousTotalDue: number;
  paymentOrderInProgress: boolean;
  paymentLink: string;
};

export type Grade = {
  name: string;
  id: number;
  fee: number;
  updatedFee: number;
  sections: Section[] | undefined;
};

export type Section = {
  id: number;
  name: string;
};

export type MonthlyFee = {
  month: string;
  monthShortName: string;
  sumOfFee: string;
  sumOfDiscount: string;
};
export type DailyFee = {
  month: string;
  day: string;
  monthShortName: string;
  sumOfFee: string;
};

export type GradesDue = {
  gradeId: number;
  gradeName: string;
  sumOfDues: number;
  sumOfFees: number;
  gradeNumericalValue: string;
};
export type Dashboard = {
  totalStudents: number;
  activeStudents: number;
  inActiveStudents: number;
  totalFeeDues: number;
  totalFeesCollected: number;
  totalDiscountGiven: number;
  dailyFeeCollection: DailyFee[];
  monthlyFeeCollection: MonthlyFee[];
  feesAndDuesByGrade: GradesDue[];
  academicYear: string;
  previousTotalDue: number;
  absenteesByGrade: [{ grade: string; sections: [{ name: string; total: number }] }];
  totalFailedAbsentMessages: number;
  smsWalletBalance: number;
  facultyAbsenteesByDept: FacultyAttendance[];
  activeAdmissions: number;
  attentionItems?: { id: string; attentionSeverity: string; metric: string }[];
};

export type UserDTO = {
  name: string;
  loginId: string;
  password: string;
  email: string;
  gender: string;
  mobile: string;
  aadhaarNumber: number;
  roles: string[];
  grades: string[];
  admissionNo: string;
};
export type Role = {
  name: string;
};

// Attendance
export interface AbsenteeEntry {
  key: string;
  value: string;
}

export type AttendanceDTO = {
  name: string;
  admissionNo: string;
  attendance: string;
};

export type AbsenteesBySectionDTO = {
  sectionName: string;
  absentees: number;
};
export type Enquiry = {
  name: string;
  dob: string;
  gender: string;
  grade: string;
  createdOn: string;
  currentSchool: string;
  guardianName: string;
  mobileNo: number;
  state: string;
};

export type EnquiryPage = {
  content: Enquiry[];
  totalElements: number;
  totalPages: number;
};

export interface MUITProps {
  rows: MyTRow[];
}

export type MUITKeys = {
  name: string;
};

export interface MaterialUITableDataItem {
  [key: string]: any; // Allows any key-value pair (string only for simplicity)
}

export interface MyTRow {
  name: string;
  calories: string;
  fat: string;
  carbs: string;
  protein: string;
}

export type StudentDashboardDTO = {
  fees: Fee[];
  feeDues: FeeDue[];
  totalFees: number;
  totalFeeDues: number;
  previousAYsFeeDueDTOS: FeeDue[];
  previousAYsTotalFeeDues: number;
};

export const TEXT_HEADING_NAME = {
  STUDENT_DASHBOARD_HEADING: "STUDENT_DASHBOARD_HEADING",
  PAYMENT_FAILED_HEADING: "PAYMENT_FAILED_HEADING",
  PLEASE_COMPLETE_PAYMENT: "PLEASE_COMPLETE_PAYMENT",
  FATHER: "FATHER",
  MOTHER: "MOTHER",
};

// ----------------------------
// Generic UI states
// ----------------------------
export const ViewState = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  NO_DATA: 'no_data',
  ERROR: 'error',
} as const;

export type ViewState = typeof ViewState[keyof typeof ViewState];

// ----------------------------
// Attendance-specific states
// ----------------------------
export const AttendanceState = {
  REQUIRED: 'attendance_required',
  ALREADY_SUBMITTED: 'attendance_already_submitted',
  SUBMITTED: 'attendance_successfully_submitted',
} as const;

export type AttendanceState = typeof AttendanceState[keyof typeof AttendanceState];

// ----------------------------
// Student-specific states
// ----------------------------
export const StudentState = {
  NOT_FOUND: 'students_not_found',
  INACTIVE: 'students_inactive', // example additional state
} as const;

export type StudentState = typeof StudentState[keyof typeof StudentState];

// ----------------------------
// Fee-specific states
// ----------------------------
export const FeeState = {
  NOT_PAID: 'fee_not_paid',
  PAID: 'fee_paid',
  PARTIALLY_PAID: 'fee_partially_paid',
} as const;

export type FeeState = typeof FeeState[keyof typeof FeeState];

// ----------------------------
// Combined type for each feature
// ----------------------------
export type AttendanceViewState = ViewState | AttendanceState | StudentState;
export type StudentViewState = ViewState | StudentState;
export type FeeViewState = ViewState | FeeState;

//This solved the above issue LEARNING
export const RELATIONS = {
  Aunt: "Aunt",
  Brother: "Brother",
  Cousin: "Cousin",
  Father: "Father",
  Grandfather: "Grandfather",
  Grandmother: "Grandmother",
  Mother: "Mother",
  Sister: "Sister",
  Stepbrother: "Stepbrother",
  Stepfather: "Stepfather",
  Stepmother: "Stepmother",
  Stepsister: "Stepsister",
  Uncle: "Uncle",
  Other: "Other",
} as const;

export type Relation = typeof RELATIONS[keyof typeof RELATIONS];

//LEARNING
// as const makes the values string literals, not string
// Keys become readonly
// Values become 'guardian' | 'emergency'

export const STUDENT_DETAILS_VIEW = {
  Student: 'student',
  Father: 'father',
  Mother: 'mother',
  Guardian: 'guardian',
  Emergency: 'emergency',
  Address: 'address',
  FeeDues: 'feeDues',
  Fees: 'fees'
} as const;

export type StudentDetailsView = typeof STUDENT_DETAILS_VIEW[keyof typeof STUDENT_DETAILS_VIEW];

export const USERS_VIEW = {
  Add: 'add',
  ADDED: 'added',
} as const;

export type UsersView = typeof USERS_VIEW[keyof typeof USERS_VIEW];

export interface StudentFeeSummary {
  name: string;
  admissionNo: string;
  guardianMobile: string;
  totalFee: number;
}

// Enum for AttendanceStatus
enum AttendanceStatus {
  PRESENT = 'PRESENT',
  ABSENT = 'ABSENT',
  LATE = 'LATE',
}

// Type for StatusDTO
interface StatusDTO {
  dayMonth: number;         // Day of the month (1-31)
  value: AttendanceStatus;  // Attendance status (e.g., 'Present', 'Absent', etc.)
}

// Type for AttendanceStatusReportDTO
export interface AttendanceStatusReportDTO {
  name: string;             // Name of the student
  fatherName: string;     // Name of the father
  attendance: StatusDTO[];  // List of StatusDTO objects
}

export const ID_CARD_VIEW = {
  Add: 'add',
  ADDED: 'added',
} as const;

// Allowed log levels for a tenant, most to least severe. The backend accepts only these values.
export const LOGGING_LEVELS = ["ERROR", "WARN", "INFO", "DEBUG"] as const;
export type LoggingLevel = (typeof LOGGING_LEVELS)[number];

// One JSON for all tenant settings: GET returns all of it, PATCH sends only the changed keys.
export type SystemSettings = {
  loggingLevel: LoggingLevel;
  attendanceMessageSendingEnabled: boolean;
  // Set by the backend while loggingLevel is DEBUG: when it automatically returns to INFO (ISO date-time).
  loggingLevelResetAt?: string | null;
};

// The keys an admin can change (loggingLevelResetAt is read-only, managed by the backend).
export type EditableSystemSettings = Pick<SystemSettings, "loggingLevel" | "attendanceMessageSendingEnabled">;

export type IdCardView = typeof ID_CARD_VIEW[keyof typeof ID_CARD_VIEW];
